import { getUserAttributes, isUserLoggedIn } from '@noths/polaris-client-user-configuration';
import { handleFetchResponse } from '@noths/polaris-client-utils';
import { createAsyncThunk } from '@reduxjs/toolkit';
import fetch from 'isomorphic-fetch';
import getConfig from 'next/config';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { NextConfig } from 'src/types';
import { EnquiriesAPIPostData, PartnerStorefrontState } from '../types';
const { publicRuntimeConfig } = getConfig() as NextConfig;

export interface ContactPartnerSubmitPayload {
  fullName: string;
  email: string;
  message: string;
  marketingOptIn?: boolean;
}

export type MarketingPreferencesSubmitPayload = Pick<ContactPartnerSubmitPayload, 'email'>;

export const contactPartnerSubmit = createAsyncThunk<
  null,
  ContactPartnerSubmitPayload,
  { rejectValue: PartnerStorefrontState['error'] }
>(
  'partnerStorefront/contactPartnerSubmit',
  async ({ fullName, email, message, marketingOptIn }, { getState, dispatch, rejectWithValue }) => {
    const { product } = getState() as ReduxApplicationState;
    const enquiriesUrl = `${publicRuntimeConfig.endpoints.enquiries}${product.code}/web_enquiries`;

    const postData: EnquiriesAPIPostData = {
      name: fullName,
      email,
      message,
    };

    if (await isUserLoggedIn()) {
      const { email: emailFromAccount, name, family_name } = (await getUserAttributes()) || {};

      if (emailFromAccount) {
        postData.email = emailFromAccount!;
      }

      if (name && family_name) {
        postData.name = `${name} ${family_name}`;
      }
    }

    if (marketingOptIn) {
      await dispatch(submitMarketingPreferences({ email: postData.email }));
    }

    const apiResponse = await fetch(enquiriesUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(postData),
    });

    if (apiResponse.status === 422) {
      const { errors } = await apiResponse.json();

      return rejectWithValue(errors);
    }

    return await handleFetchResponse(apiResponse);
  },
);

export const submitMarketingPreferences = createAsyncThunk<null, MarketingPreferencesSubmitPayload>(
  'partnerStorefront/submitMarketingPreferences',
  async ({ email }) => {
    const marketingPreferencesAnonymousUrl = `${publicRuntimeConfig.hosts.communicationPreferences}/anonymous`;

    return await fetch(marketingPreferencesAnonymousUrl, {
      method: 'POST',
      body: JSON.stringify({
        address: email,
        channel: 'email',
        status: 'ENABLED',
        source: 'ASK_SELLER',
      }),
    }).then(handleFetchResponse);
  },
);
